import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  Store,
  Palette,
  Globe,
  Settings,
  Save,
  ArrowLeft,
  Image as ImageIcon,
  Package,
  Eye,
  Trash2 } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

export const StoreEditor = () => {const { t } = useTranslation("ecommerce");
  const { storeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialTab = searchParams.get('tab') || 'general';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [store, setStore] = useState(null);

  // Form States
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    logo_url: '',
    currency: 'BRL',
    theme_config: {
      primary_color: '#10b981',
      secondary_color: '#0d9488',
      accent_color: '#f59e0b'
    },
    meta_title: '',
    meta_description: ''
  });

  useEffect(() => {
    fetchStore();
  }, [storeId]);

  const fetchStore = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ecommerce/stores/${storeId}`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setStore(data);
        setFormData({
          name: data.name || '',
          slug: data.slug || '',
          description: data.description || '',
          logo_url: data.logo_url || '',
          currency: data.currency || 'BRL',
          theme_config: data.theme_config || {
            primary_color: '#10b981',
            secondary_color: '#0d9488',
            accent_color: '#f59e0b'
          },
          meta_title: data.meta_title || '',
          meta_description: data.meta_description || ''
        });
      } else {
        toast.error('Store not found');
        navigate('/ecommerce/stores');
      }
    } catch (err) {
      toast.error('Failed to load store');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleThemeChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      theme_config: { ...prev.theme_config, [field]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/ecommerce/stores/${storeId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Store updated successfully!');
        fetchStore();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update store');
      }
    } catch (err) {
      toast.error('Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this store? This action cannot be undone.')) return;

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/ecommerce/stores/${storeId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        toast.success('Store deleted successfully');
        navigate('/ecommerce/stores');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to delete store');
      }
    } catch (err) {
      toast.error('Error deleting store');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
            </div>);

  }

  return (
    <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/ecommerce/stores')}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] capitalize">
                            {store?.name}
                        </h1>
                        <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{t("id", "ID:")}{storeId}</Badge>
                            <Badge variant="secondary" className="text-xs">{t("store", "/store/")}{store?.slug}</Badge>
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <a href={`/store/${store?.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" className="gap-2">
                            <Eye className="h-4 w-4" />{t("preview", "Preview")}

            </Button>
                    </a>
                    <Button
            className="gap-2 bg-gradient-to-r from-emerald-500 to-teal-600"
            onClick={handleSave}
            disabled={saving}>
            
                        <Save className="h-4 w-4" />
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            <Tabs defaultValue={initialTab} className="w-full">
                <TabsList className="bg-muted/50 p-1">
                    <TabsTrigger value="general" className="gap-2">
                        <Store className="h-4 w-4" />{t("general", "General")}

          </TabsTrigger>
                    <TabsTrigger value="theme" className="gap-2">
                        <Palette className="h-4 w-4" />{t("theme", "Theme")}

          </TabsTrigger>
                    <TabsTrigger value="seo" className="gap-2">
                        <Globe className="h-4 w-4" />{t("seo", "SEO")}

          </TabsTrigger>
                    <TabsTrigger value="settings" className="gap-2">
                        <Settings className="h-4 w-4" />{t("settings", "Settings")}

          </TabsTrigger>
                </TabsList>

                <div className="mt-6">
                    <TabsContent value="general">
                        <Card className="card-elevated">
                            <CardHeader>
                                <CardTitle>{t("generalInformation", "General Information")}</CardTitle>
                                <CardDescription>{t("basicStoreIdentity", "Basic store identity and identification")}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">{t("storeName", "Store Name")}</label>
                                        <input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="My Store Name" />
                    
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">{t("storeSlugUrl", "Store Slug (URL)")}</label>
                                        <div className="flex">
                                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 bg-gray-50 text-gray-500 text-sm">{t("store", "/store/")}

                      </span>
                                            <input
                        name="slug"
                        value={formData.slug}
                        onChange={handleInputChange}
                        className="flex-1 px-4 py-2 border rounded-r-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="my-store-url" />
                      
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">{t("description", "Description")}</label>
                                    <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 min-h-[100px]"
                    placeholder="Tell your customers about your store..." />
                  
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">{t("logoUrl", "Logo URL")}</label>
                                    <div className="flex gap-4 items-center">
                                        <div className="h-16 w-16 bg-muted rounded-lg flex items-center justify-center overflow-hidden border">
                                            {formData.logo_url ?
                      <img src={formData.logo_url} alt="Logo Prev" className="w-full h-full object-cover" /> :

                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                      }
                                        </div>
                                        <input
                      name="logo_url"
                      value={formData.logo_url}
                      onChange={handleInputChange}
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                      placeholder="https://example.com/logo.png" />
                    
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="theme">
                        <Card className="card-elevated">
                            <CardHeader>
                                <CardTitle>{t("themeCustomization", "Theme Customization")}</CardTitle>
                                <CardDescription>{t("personalizeYourStores", "Personalize your store's look and feel")}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid gap-6 md:grid-cols-3">
                                    <div className="space-y-3">
                                        <label className="text-sm font-medium block">{t("primaryColor", "Primary Color")}</label>
                                        <div className="flex items-center gap-3">
                                            <input
                        type="color"
                        value={formData.theme_config.primary_color}
                        onChange={(e) => handleThemeChange('primary_color', e.target.value)}
                        className="h-10 w-20 rounded cursor-pointer border-0 p-0" />
                      
                                            <span className="font-mono text-xs uppercase">{formData.theme_config.primary_color}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground">{t("mainButtonsAccents", "Main buttons, accents, and branding")}</p>
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-sm font-medium block">{t("secondaryColor", "Secondary Color")}</label>
                                        <div className="flex items-center gap-3">
                                            <input
                        type="color"
                        value={formData.theme_config.secondary_color}
                        onChange={(e) => handleThemeChange('secondary_color', e.target.value)}
                        className="h-10 w-20 rounded cursor-pointer border-0 p-0" />
                      
                                            <span className="font-mono text-xs uppercase">{formData.theme_config.secondary_color}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground">{t("footerAndSecondary", "Footer and secondary elements")}</p>
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-sm font-medium block">{t("accentColor", "Accent Color")}</label>
                                        <div className="flex items-center gap-3">
                                            <input
                        type="color"
                        value={formData.theme_config.accent_color}
                        onChange={(e) => handleThemeChange('accent_color', e.target.value)}
                        className="h-10 w-20 rounded cursor-pointer border-0 p-0" />
                      
                                            <span className="font-mono text-xs uppercase">{formData.theme_config.accent_color}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground">{t("highlightsAndEmphasis", "Highlights and emphasis points")}</p>
                                    </div>
                                </div>

                                {/* Preview Card */}
                                <div className="mt-8 p-6 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">{t("liveThemePreview", "Live Theme Preview")}</p>
                                    <div className="bg-white rounded-xl shadow-sm overflow-hidden border">
                                        <div
                      className="h-12 flex items-center px-4 text-white"
                      style={{ backgroundColor: formData.theme_config.primary_color }}>
                      
                                            <span className="font-bold">{t("storeNavbar", "Store Navbar")}</span>
                                        </div>
                                        <div className="p-4 space-y-4">
                                            <div className="flex gap-2">
                                                <div
                          className="px-4 py-2 rounded-lg text-white text-sm"
                          style={{ backgroundColor: formData.theme_config.primary_color }}>{t("buttonPrimary", "Button Primary")}


                        </div>
                                                <div
                          className="px-4 py-2 rounded-lg text-sm border"
                          style={{ borderColor: formData.theme_config.secondary_color, color: formData.theme_config.secondary_color }}>{t("buttonAlt", "Button Alt")}


                        </div>
                                            </div>
                                            <div className="h-4 w-3/4 bg-slate-100 rounded" />
                                            <div className="h-4 w-1/2 bg-slate-100 rounded" style={{ backgroundColor: `${formData.theme_config.accent_color}20` }} />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="seo">
                        <Card className="card-elevated">
                            <CardHeader>
                                <CardTitle>{t("seoSearchOptions", "SEO & Search Options")}</CardTitle>
                                <CardDescription>{t("controlHowYour", "Control how your store appears in search engines")}</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">{t("metaTitle", "Meta Title")}</label>
                                    <input
                    name="meta_title"
                    value={formData.meta_title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="Search Engine Result Title" />
                  
                                    <p className="text-xs text-muted-foreground">{t("ideallyBetween5060", "Ideally between 50-60 characters")}</p>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium">{t("metaDescription", "Meta Description")}</label>
                                    <textarea
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 min-h-[100px]"
                    placeholder="Brief summary of your store for search results..." />
                  
                                    <p className="text-xs text-muted-foreground">{t("ideallyBetween150160", "Ideally between 150-160 characters")}</p>
                                </div>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="settings">
                        <div className="grid gap-6 md:grid-cols-2">
                            <Card className="card-elevated">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <Package className="h-5 w-5 text-indigo-600" />{t("catalogManagement", "Catalog Management")}

                  </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-sm text-muted-foreground">{t("manageWhichProducts", "Manage which products are active in this storefront.")}

                  </p>
                                    <Link to={`/ecommerce/products?store=${storeId}`}>
                                        <Button variant="outline" className="w-full gap-2">
                                            <Package className="h-4 w-4" />{t("managePublishedProducts", "Manage Published Products")}

                    </Button>
                                    </Link>
                                </CardContent>
                            </Card>

                            <Card className="card-elevated border-red-100 dark:border-red-900/30">
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2 text-red-600">
                                        <Trash2 className="h-5 w-5" />{t("dangerZone", "Danger Zone")}

                  </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-sm text-muted-foreground">{t("deletingAStore", "Deleting a store is permanent and cannot be undone. All store configurations will be lost.")}

                  </p>
                                    <Button
                    variant="destructive"
                    className="w-full gap-2"
                    onClick={handleDelete}
                    disabled={saving}>
                    
                                        <Trash2 className="h-4 w-4" />
                                        {saving ? 'Deleting...' : 'Delete Store'}
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </TabsContent>
                </div>
            </Tabs>
        </div>);

};

export default StoreEditor;